import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getVesselBoilerFuelConsumption } from "../../../../services/vesselMachineryService";
import { generateDateTicks } from "../../../../services/dataTicks";
import { SlidersHorizontal } from "lucide-react";

const FuelConsAnalysisChart = () => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);

  const [fuelConsumptionData, setFuelConsumptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFuels, setSelectedFuels] = useState([
    "HFO",
    "LFO",
    "MGO",
    "others",
  ]);

  // Define color codes for each fuel type
  const fuelColors = {
    HFO: "#d35400", // Orange for Heavy Fuel Oil
    LFO: "#1a5276", // Blue for Light Fuel Oil
    MGO: "#2ecc71", // Green for Marine Gas Oil
    others: "#9b59b6", // Purple for Others
  };

  // Fuel type labels
  const fuelLabels = {
    HFO: "Heavy Fuel Oil",
    LFO: "Light Fuel Oil",
    MGO: "Marine Gas Oil",
    others: "Others",
  };

  // Fetch fuel consumption data from API
  useEffect(() => {
    const fetchFuelConsumptionData = async () => {
      if (!currentVessel?._id) {
        setFuelConsumptionData(null);
        return;
      }

      if (!selectedVoyage && (!startDate || !endDate)) {
        setFuelConsumptionData(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getVesselBoilerFuelConsumption(
          currentVessel._id,
          selectedVoyage ? null : startDate,
          selectedVoyage ? null : endDate,
          selectedVoyage?._id || null
        );

        console.log("API Response Fuel Cons Analysis:", response);

        if (response?.status === "success") {
          setFuelConsumptionData(response.data);
        } else {
          setError(
            response?.message || "Failed to fetch fuel consumption data"
          );
          setFuelConsumptionData(null);
        }
      } catch (err) {
        console.error("Error fetching fuel consumption data:", err);
        setError(err.message);
        setFuelConsumptionData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFuelConsumptionData();
  }, [currentVessel, startDate, endDate, selectedVoyage]);

  // Transform API data to Highcharts format
  const transformDataForChart = () => {
    if (
      !fuelConsumptionData?.dailyData ||
      fuelConsumptionData.dailyData.length === 0
    ) {
      return {
        dates: [],
        series: [],
      };
    }

    const dates = fuelConsumptionData.dailyData.map((item) => item.date);
    const seriesData = {
      HFO: [],
      LFO: [],
      MGO: [],
      others: [],
    };

    fuelConsumptionData.dailyData.forEach((dayData) => {
      const timestamp = new Date(dayData.date).getTime();
      const fullDate = new Date(dayData.fullDate).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      seriesData.HFO.push({
        x: timestamp,
        y: dayData.HFO || 0,
        fullDate: fullDate,
        color: fuelColors.HFO,
      });

      seriesData.LFO.push({
        x: timestamp,
        y: dayData.LFO || 0,
        fullDate: fullDate,
        color: fuelColors.LFO,
      });

      seriesData.MGO.push({
        x: timestamp,
        y: dayData.MGO || 0,
        fullDate: fullDate,
        color: fuelColors.MGO,
      });

      seriesData.others.push({
        x: timestamp,
        y: dayData.others || 0,
        fullDate: fullDate,
        color: fuelColors.others,
      });
    });

    return {
      dates,
      series: selectedFuels.map((fuelType) => ({
        name: fuelLabels[fuelType],
        data: seriesData[fuelType],
        color: fuelColors[fuelType],
      })),
    };
  };

  const chartData = transformDataForChart();
  const tickPositions =
    chartData.dates && chartData.dates.length > 0
      ? generateDateTicks(chartData.dates)
      : [];

  const options = {
    chart: {
      type: "line",
      height: "500",
      backgroundColor: "transparent",
      spacing: [20, 15, 15, 15],
      style: {
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      },
    },
    title: null,
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: {
        text: null,
      },
      labels: {
        format: "{value:%b %e}",
        style: {
          color: "#666666",
          fontSize: "11px",
        },
        rotation: -45,
      },
      gridLineWidth: 1,
      gridLineColor: "#f0f0f0",
    },
    yAxis: {
      title: {
        text: "Consumption (MT/Day)",
        style: {
          color: "#718096",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      min: 0,
      startOnTick: true,
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
      labels: {
        style: {
          color: "#718096",
        },
      },
    },
    plotOptions: {
      area: {
        stacking: null,
        lineWidth: 2,
        marker: {
          enabled: true,
          symbol: "circle",
          radius: 3,
          lineWidth: 1,
          lineColor: "#ffffff",
        },
        states: {
          hover: {
            enabled: true,
            lineWidth: 3,
          },
        },
      },
    },
    tooltip: {
      shared: true,
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderColor: "#e2e8f0",
      borderRadius: 8,
      shadow: true,
      style: {
        fontSize: "12px",
      },
      headerFormat:
        '<span style="font-size: 13px; font-weight: 600; color: #1a365d">{point.key}</span><br/>',
      pointFormatter: function () {
        return `<span style="color:${this.color}">●</span> ${this.series.name
          }: <b>${this.y.toFixed(1)} MT/day</b><br/>`;
      },
    },
    series: chartData.series,
    credits: {
      enabled: false,
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#566573",
        fontSize: "11px",
        fontWeight: "500",
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
          },
        },
      ],
    },
  };
  const toggleFuelType = (fuelType) => {
    setSelectedFuels((prev) =>
      prev.includes(fuelType)
        ? prev.filter((f) => f !== fuelType)
        : [...prev, fuelType]
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a5276]"></div>
            <p className="mt-3 text-gray-600 font-medium">
              Loading fuel consumption data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-50 rounded-full mb-3">
              <svg
                className="w-6 h-6 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Unable to load data
            </h3>
            <p className="text-gray-600 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // No data selected state
  if (!fuelConsumptionData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No vessel or filter selected</div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Fuel Type Selector */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
          Select by:
        </label>
        <div className="flex flex-wrap gap-3">
          {Object.entries(fuelLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleFuelType(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedFuels.includes(key)
                  ? "text-white shadow-sm"
                  : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                }`}
              style={{
                backgroundColor: selectedFuels.includes(key)
                  ? fuelColors[key]
                  : "",
                border: selectedFuels.includes(key)
                  ? `1px solid ${fuelColors[key]}`
                  : "1px solid #e2e8f0",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart - Always show if we have data */}
      {fuelConsumptionData &&
        fuelConsumptionData.dailyData &&
        fuelConsumptionData.dailyData.length > 0 ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-gray-500">No chart data available</div>
        </div>
      )}
    </div>
  );
};

export default FuelConsAnalysisChart;
